/**
 * XIN NGHỈ.
 *
 * POST /staff/leave-requests creates the request; GET /staff/leave-requests
 * lists the employee's own requests with their approval status — both wired
 * below. The attachment (giấy khám bệnh…) is a REAL upload: capture → compress
 * → POST /files → `attachmentFileId` rides the request body.
 *
 * `totalDaysBp` in the response is basis points of a day (10 000 = one full
 * day), which is what the payroll module consumes; the screen converts it back
 * to days for display.
 */
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { errorMessage, queryKeys, useLeaveRequests } from '../lib/queries';
import { LEAVE_TYPE_LABEL, approvalStatusLabel, approvalStatusTone } from '../lib/labels';
import type { LeaveRequestItem, LeaveRequestResponse, LeaveType } from '../lib/contracts';
import { formatTimeDate, toDateInput } from '../lib/format';
import {
  Banner,
  Button,
  Card,
  EmptyState,
  LoadingState,
  SectionHeader,
  StatusBadge,
} from '../components/ui';
import { ScreenHeader } from '../components/ScreenHeader';
import { EvidenceUploader } from '../components/EvidenceUploader';
import { useToast } from '../components/Toast';
import { OfflineDisabledNote } from '../components/SyncStatus';
import { useSyncState } from '../lib/hooks';

const LEAVE_TYPES = Object.keys(LEAVE_TYPE_LABEL) as readonly LeaveType[];
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const schema = z
  .object({
    leaveType: z.enum(['ANNUAL', 'SICK', 'UNPAID', 'MATERNITY', 'EMERGENCY', 'OTHER']),
    fromDate: z.string().regex(DATE_RE, 'Chọn ngày bắt đầu.'),
    toDate: z.string().regex(DATE_RE, 'Chọn ngày kết thúc.'),
    halfDay: z.boolean(),
    halfDayPart: z.enum(['MORNING', 'AFTERNOON']),
    // Mirrors the server's `min(5).max(1000)` so the user is told before the
    // round trip rather than by a 400.
    reason: z.string().min(5, 'Lý do phải có ít nhất 5 ký tự.').max(1000),
  })
  .refine((values) => values.toDate >= values.fromDate, {
    message: 'Ngày kết thúc phải sau hoặc bằng ngày bắt đầu.',
    path: ['toDate'],
  });

type FormValues = z.infer<typeof schema>;

export function LeaveRequestScreen(): JSX.Element {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { online } = useSyncState();
  const [created, setCreated] = useState<LeaveRequestResponse | null>(null);
  const [attachmentFileId, setAttachmentFileId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [formGeneration, setFormGeneration] = useState(0);
  const history = useLeaveRequests();

  const today = toDateInput(new Date());
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      leaveType: 'ANNUAL',
      fromDate: today,
      toDate: today,
      halfDay: false,
      halfDayPart: 'MORNING',
      reason: '',
    },
  });

  const halfDay = watch('halfDay');
  const leaveType = watch('leaveType');

  const submit = useMutation({
    mutationFn: (values: FormValues) =>
      api.createLeaveRequest({
        leaveType: values.leaveType,
        fromDate: values.fromDate,
        toDate: values.toDate,
        halfDay: values.halfDay,
        halfDayPart: values.halfDay ? values.halfDayPart : null,
        reason: values.reason,
        attachmentFileId,
      }),
    onSuccess: (response) => {
      setCreated(response);
      reset({ ...watch(), reason: '' });
      setAttachmentFileId(null);
      setFormGeneration((generation) => generation + 1);
      void queryClient.invalidateQueries({ queryKey: queryKeys.leaveRequests });
      toast.success('Đã gửi đơn xin nghỉ', 'Đơn đang chờ quản lý duyệt.');
    },
    onError: (error) => toast.error('Không gửi được đơn', errorMessage(error)),
  });

  const onSubmit = handleSubmit((values) => submit.mutate(values));

  return (
    <div className="pb-8">
      <ScreenHeader title="Xin nghỉ" />

      {created === null ? null : (
        <Banner tone="success" title="Đơn đã được gửi">
          Mã đơn <strong>{created.leaveRequestId}</strong> · trạng thái {created.status} ·{' '}
          {(created.totalDaysBp / 10_000).toLocaleString('vi-VN', { maximumFractionDigits: 2 })} ngày công.
        </Banner>
      )}

      <form onSubmit={(event) => void onSubmit(event)} className="mt-3 space-y-4">
        <Card>
          <p className="label mb-2">Loại nghỉ</p>
          <div className="grid grid-cols-2 gap-2">
            {LEAVE_TYPES.map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => setValue('leaveType', type, { shouldValidate: true })}
                className={[
                  'min-h-touch rounded-2xl border px-3 text-sm font-semibold active:bg-slate-50',
                  type === leaveType ? 'border-brand-500 bg-brand-50 text-brand-800' : 'border-slate-200 text-slate-700',
                ].join(' ')}
              >
                {LEAVE_TYPE_LABEL[type]}
              </button>
            ))}
          </div>
          {leaveType === 'UNPAID' ? (
            <p className="mt-2 rounded-xl bg-amber-50 p-3 text-sm text-amber-900">
              Nghỉ không lương sẽ không được tính công trong kỳ lương.
            </p>
          ) : null}
        </Card>

        <Card>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label" htmlFor="fromDate">
                Từ ngày
              </label>
              <input id="fromDate" type="date" className="field mt-1" {...register('fromDate')} />
              {errors.fromDate ? <p className="field-error">{errors.fromDate.message}</p> : null}
            </div>
            <div>
              <label className="label" htmlFor="toDate">
                Đến ngày
              </label>
              <input id="toDate" type="date" className="field mt-1" {...register('toDate')} />
              {errors.toDate ? <p className="field-error">{errors.toDate.message}</p> : null}
            </div>
          </div>

          <label className="mt-4 flex min-h-touch items-center gap-3">
            <input type="checkbox" className="h-6 w-6 rounded" {...register('halfDay')} />
            <span className="font-medium text-slate-800">Chỉ nghỉ nửa ngày</span>
          </label>

          {halfDay ? (
            <div className="mt-2 grid grid-cols-2 gap-2">
              {(['MORNING', 'AFTERNOON'] as const).map((part) => (
                <label
                  key={part}
                  className="flex min-h-touch items-center justify-center gap-2 rounded-2xl border border-slate-200 px-3"
                >
                  <input type="radio" value={part} {...register('halfDayPart')} />
                  <span>{part === 'MORNING' ? 'Buổi sáng' : 'Buổi chiều'}</span>
                </label>
              ))}
            </div>
          ) : null}
        </Card>

        <Card>
          <label className="label" htmlFor="reason">
            Lý do
          </label>
          <textarea
            id="reason"
            rows={4}
            className="field mt-1"
            placeholder="Ví dụ: về quê giỗ ông, đã nhờ bạn Hùng làm thay ca chiều."
            {...register('reason')}
          />
          {errors.reason ? <p className="field-error">{errors.reason.message}</p> : null}

          <div className="mt-3">
            <EvidenceUploader
              key={formGeneration}
              label="Ảnh giấy tờ (không bắt buộc)"
              hint="Giấy khám bệnh, giấy xác nhận… chụp rõ nét để quản lý duyệt nhanh."
              purpose="ATTACHMENT"
              onChange={setAttachmentFileId}
              onBusyChange={setUploading}
            />
          </div>
        </Card>

        <OfflineDisabledNote what="gửi đơn xin nghỉ" />

        <Button
          type="submit"
          size="lg"
          fullWidth
          loading={submit.isPending}
          disabled={!online || uploading}
        >
          {uploading ? 'Đang tải ảnh…' : 'Gửi đơn xin nghỉ'}
        </Button>
      </form>

      {/* --- history: GET /staff/leave-requests -------------------------- */}
      <SectionHeader title="Đơn đã gửi" />
      {history.isLoading ? (
        <LoadingState />
      ) : (history.data?.leaveRequests.length ?? 0) === 0 ? (
        <EmptyState title="Bạn chưa gửi đơn xin nghỉ nào" />
      ) : (
        <ul className="space-y-2">
          {(history.data?.leaveRequests ?? []).map((request) => (
            <li key={request.id}>
              <LeaveRequestCard request={request} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function LeaveRequestCard({ request }: { readonly request: LeaveRequestItem }): JSX.Element {
  const typeLabel =
    request.leaveType in LEAVE_TYPE_LABEL
      ? LEAVE_TYPE_LABEL[request.leaveType as LeaveType]
      : request.leaveType;
  const days = (request.totalDaysBp / 10_000).toLocaleString('vi-VN', { maximumFractionDigits: 2 });

  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-bold text-slate-900">{typeLabel}</p>
          <p className="text-sm text-slate-600">
            {request.fromDate === request.toDate ? request.fromDate : `${request.fromDate} → ${request.toDate}`}
            {' · '}
            {request.halfDay ? 'nửa ngày' : `${days} ngày công`}
            {request.isPaid ? '' : ' · không lương'}
          </p>
        </div>
        <StatusBadge tone={approvalStatusTone(request.status)}>{approvalStatusLabel(request.status)}</StatusBadge>
      </div>
      <p className="mt-2 text-sm text-slate-700">{request.reason}</p>
      {request.decisionNote === null ? null : (
        <p className="mt-2 rounded-xl bg-slate-50 p-3 text-sm text-slate-700">
          <strong>Phản hồi của quản lý:</strong> {request.decisionNote}
        </p>
      )}
      <p className="mt-1 text-xs text-slate-400">
        Gửi lúc {formatTimeDate(request.submittedAt)}
        {request.decidedAt === null ? '' : ` · xử lý lúc ${formatTimeDate(request.decidedAt)}`}
      </p>
    </Card>
  );
}
